import type { Coordinate } from '@studio/js-utils/geometry';
import {
  BoundingBoxShadowStrokeWidth,
  BoundingBoxStrokeWidth,
  CursorShadowStrokeWidth,
  CursorSize,
  CursorStrokeWidth,
  NotchWidth,
  ShadowOpacity
} from './constants.js';
import type { ClickToCenterDrawingContext } from './types.js';

/**
 * Draw the cursor.
 *
 * @param context - The drawing context.
 * @param position - The cursor position.
 * @param useCine - Should use cine.
 */
export const drawCursor = (
  context: ClickToCenterDrawingContext,
  position?: Coordinate,
  useCine?: boolean
): void => {
  const { clickToCenterSelectPosition, palette, scale, selected, surface } = context;

  if (!position || !clickToCenterSelectPosition) {
    return;
  }

  let { x, y } = clickToCenterSelectPosition;

  // Get the width and height of the canvas
  const canvasWidth = surface.canvas.width / devicePixelRatio;
  const canvasHeight = surface.canvas.height / devicePixelRatio;

  // Calculate the width and height of the bounding box
  const width = canvasWidth * scale;
  const height = canvasHeight * scale;

  x -= width / 2;
  y -= height / 2;

  const strokeColor = selected
    ? useCine
      ? palette.warning.toRgbString()
      : palette.success.toRgbString()
    : palette.white.toRgbString();

  const plusStrokeColor = useCine ? palette.warning.toRgbString() : strokeColor;

  // Draw the bounding box shadow
  surface.beginPath();
  surface.strokeStyle = palette.black.alpha(ShadowOpacity).toRgbString();
  surface.lineWidth = BoundingBoxShadowStrokeWidth;
  surface.rect(x, y, width, height);
  surface.stroke();
  surface.closePath();

  // Draw the bounding box
  surface.beginPath();
  surface.strokeStyle = strokeColor;
  surface.lineWidth = BoundingBoxStrokeWidth;
  surface.rect(x, y, width, height);
  surface.stroke();
  surface.closePath();

  // Hollow out horizontal notch over bounding box
  surface.beginPath();

  surface.clearRect(
    x - NotchWidth / 2,
    y + height / 2 - NotchWidth / 2,
    width + NotchWidth,
    NotchWidth
  );

  surface.closePath();

  // Hollow out vertical notch over bounding box
  surface.beginPath();

  surface.clearRect(
    x + width / 2 - NotchWidth / 2,
    y - NotchWidth / 2,
    NotchWidth,
    height + NotchWidth
  );

  surface.closePath();

  // Draw the shadow of the cursor
  surface.beginPath();
  surface.strokeStyle = palette.black.alpha(ShadowOpacity).toRgbString();
  surface.lineWidth = CursorShadowStrokeWidth;

  // Draw the cursor as a plus sign
  surface.rect(
    x + width / 2 - CursorShadowStrokeWidth / 4 - CursorSize / 2,
    y + height / 2 - CursorShadowStrokeWidth / 2,
    CursorSize + CursorShadowStrokeWidth / 2,
    CursorShadowStrokeWidth
  );

  surface.rect(
    x + width / 2 - CursorShadowStrokeWidth / 2,
    y + height / 2 - CursorShadowStrokeWidth / 4 - CursorSize / 2,
    CursorShadowStrokeWidth,
    CursorSize + CursorShadowStrokeWidth / 2
  );

  surface.stroke();
  surface.closePath();

  // Draw the cursor
  surface.beginPath();
  surface.strokeStyle = plusStrokeColor;
  surface.lineWidth = CursorStrokeWidth;

  // Draw the cursor as a plus sign
  surface.rect(
    x + width / 2 - CursorSize / 2,
    y + height / 2 - CursorStrokeWidth / 2,
    CursorSize,
    CursorStrokeWidth
  );

  surface.rect(
    x + width / 2 - CursorStrokeWidth / 2,
    y + height / 2 - CursorSize / 2,
    CursorStrokeWidth,
    CursorSize
  );

  surface.stroke();
  surface.closePath();
};
